import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { selectIsIra } from '../../../account-info-header/reducers';
import { Country } from '../../../create-request/models/shared/Country';
import { ReceivingAccountHolder, ReceivingAccountHolderIso, TaxRemittance, WireInfo } from '../../../create-request/models/wire/WireInfo';
import { buildFromPrefilledForm } from '../../../create-request/utilities/formUtilities';
import { ModifiedPayeeNames } from '../../../modified-first-parties/models/FirstParties';
import { selectModifiedFirstPartyPayeesFormatted } from '../../../modified-first-parties/reducers';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { Constants, WireAbaCodes } from '../../../shared/utilities/Constants';
import { StandingAuthFormActions, StandingAuthReviewPageActions } from '../../actions';
import { StandingAuthFormServiceError, WireStandingAuthDetails } from '../../models/standingAuthorizationForm';
import { missingThirdPartyAddress, selectCanDisplayCreditToInfo, selectIsFromEditInProgress, selectModifiedPayeeCheckbox, selectOpenTwePopupConditions, selectPayeeName, selectReceivingAccountHolder, selectReceivingAccountHolderIso, selectStandingAuthFormHasError, selectStandingAuthFormPrefilledState, selectStandingAuthFormServiceError, selectStandingAuthWireInfo, selectStandingAuthWireIntermediarySelection, selectWireAbaCode, selectWireCountries, selectWireDetailsErrorStandingAuth, selectWireDetailsWarningStandingAuth, selectWireIsoModifiedPayeeName, selectWireModifiedPayeeName1, selectWireModifiedPayeeName2, selectWireRecipientSelection, selectTaxRemittance } from '../../reducers';
import { selectUseTemplate } from '../../../create-request/reducers';

@Component({
  standalone: false,
  selector: 'mm-standing-authorization-details-wire',
  templateUrl: './sa-details-wire.component.html',
  styleUrls: ['./sa-details-wire.component.scss']
})
export class StandingAuthorizationDetailsWireComponent implements OnInit {
  @Input() parentForm!: UntypedFormGroup;
  @Input() prefilledPromptsObs : Observable<WireStandingAuthDetails>;
  standingAuthorizationDetailsGroup: UntypedFormGroup;
  useSloa$: Observable<boolean>;
  validRecipient: boolean;
  canDisplayCreditToInfo$: Observable<boolean>;
  destroy$: Subject<boolean> = new Subject();
  receivingAccountHolder$: Observable<ReceivingAccountHolder>;
  receivingAccountHolderIso$: Observable<ReceivingAccountHolderIso>;
  firstParty$: Observable<'firstParty' | 'thirdParty' | ''>;
  countryList$: Observable<Country[]>;
  missingThirdPartyAddress$: Observable<boolean>;
  wireIntermediary$: Observable<'yes' | 'no' | ''>;
  wireInfo$: Observable<WireInfo>;
  wireAbaCode$: Observable<string>;
  wireDetailsErrorStandingAuth$: Observable<string>;
  wireDetailsWarningStandingAuth$: Observable<string>;
  standingAuthFormServiceError$: Observable<StandingAuthFormServiceError>;
  hasBadRequestServiceError: boolean;
  serviceErrorList: string[];
  hasServiceError: boolean;
  intermediaryRequiredText: string = Constants.intermediaryRequiredMessage;
  abaCodes = WireAbaCodes;
  isIra$: Observable<boolean>;
  firstPartyPayees$: Observable<ModifiedPayeeNames[]>;
  payeeName$: Observable<string | undefined>;
  modifiedPayeeCheckBox$: Observable<boolean | undefined>;
  modifiedPayeeName1$: Observable<string | undefined>;
  modifiedPayeeName2$: Observable<string | undefined>;
  isoModifiedPayeeName$: Observable<string | undefined>;
  isFromEditReview: Observable<boolean>;
  showIntermediaryFields$: Observable<boolean> | undefined;
  wireDetailsWarning$: Observable<string>;
  useTemplate$: Observable<boolean>;
  isIrsAba: boolean = false;
  taxRemittance$: Observable<TaxRemittance>;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private ffs: FeatureFlagService
  ) { }

  ngOnInit(): void {
    this.standingAuthFormServiceError$ = this.store.select(selectStandingAuthFormServiceError);
    this.standingAuthFormServiceError$.pipe(takeUntil(this.destroy$)).subscribe((val: StandingAuthFormServiceError) => {
      if (val?.status?.toString().startsWith('4')) {
        this.hasBadRequestServiceError = true;
        if (val.errorList) {
          this.serviceErrorList = val.errorList.map(err => err.detail);
        }
      } else if (val?.status?.toString().startsWith('5')) {
        this.hasServiceError = true;
      } else {
        this.hasServiceError = val.hasError;
        this.hasBadRequestServiceError = false;
      }
    })

    this.standingAuthorizationDetailsGroup = this.fb.group({})
    this.parentForm.setControl('standingAuthorizationDetails', this.standingAuthorizationDetailsGroup, { emitEvent: false });
    this.wireIntermediary$ = this.store.select(selectStandingAuthWireIntermediarySelection);
    this.showIntermediaryFields$ = this.wireIntermediary$.pipe(map((selection) => selection === 'yes'));
    
    this.wireAbaCode$ = this.store.select(selectWireAbaCode);
    this.wireInfo$ = this.store.select(selectStandingAuthWireInfo);
    this.store.select(selectStandingAuthWireInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.isIrsAba = info.isIrsAba;
    });
    this.wireDetailsErrorStandingAuth$ = this.store.select(selectWireDetailsErrorStandingAuth);
    this.wireDetailsWarningStandingAuth$ = this.store.select(selectWireDetailsWarningStandingAuth);
    this.useTemplate$ = this.store.select(selectUseTemplate);
    this.isIra$ = this.store.select(selectIsIra);

    this.receivingAccountHolderIso$ = this.store.select(selectReceivingAccountHolderIso);
    this.receivingAccountHolder$ = this.store.select(selectReceivingAccountHolder);
    this.taxRemittance$ = this.store.select(selectTaxRemittance);
    this.firstParty$ = this.store.select(selectWireRecipientSelection);
    this.countryList$ = this.store.select(selectWireCountries);
    this.missingThirdPartyAddress$ = this.store.select(missingThirdPartyAddress);
    this.canDisplayCreditToInfo$ = this.store.select(selectCanDisplayCreditToInfo).pipe(takeUntil(this.destroy$), map(_canDisplayCreditInfo => _canDisplayCreditInfo));
    this.firstPartyPayees$ = this.store.select(selectModifiedFirstPartyPayeesFormatted);
    this.payeeName$ = this.store.select(selectPayeeName);
    this.modifiedPayeeCheckBox$ = this.store.select(selectModifiedPayeeCheckbox);
    this.modifiedPayeeName1$ = this.store.select(selectWireModifiedPayeeName1);
    this.modifiedPayeeName2$ = this.store.select(selectWireModifiedPayeeName2);
    this.isoModifiedPayeeName$ = this.store.select(selectWireIsoModifiedPayeeName);
    this.isFromEditReview = this.store.select(selectStandingAuthFormPrefilledState).pipe(map(state => state.name === 'edit-review'));
    
    // LOADING REVIEW PAGE
    combineLatest([
      this.store.select(selectOpenTwePopupConditions),
      this.store.select(selectStandingAuthFormHasError),
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([popupConditions, formHasError]) => {
      const { verifyCallFinished, openTwePopup } = popupConditions;
      if (verifyCallFinished && !openTwePopup && !formHasError && !this.hasServiceError && !this.hasBadRequestServiceError) {
        this.store.dispatch(StandingAuthFormActions.loadReviewPage());
      }
    });

    // REBUILD FORM FROM EDIT FLOW
    this.prefilledPromptsObs
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectIsFromEditInProgress)),
        filter(([prefilledPrompts, isFromEdit]) => !!prefilledPrompts && isFromEdit)
      )
      .subscribe(([prefilledPrompts, _isFromEdit]) => {
        buildFromPrefilledForm(
          prefilledPrompts,
          this.standingAuthorizationDetailsGroup
        );
        this.store.dispatch(StandingAuthReviewPageActions.editCompleted());
      });
  }

  isRecipientValid(status: boolean) {
    this.validRecipient = status;
  }

  get authorizationNickname(): UntypedFormControl {
    return this.parentForm.controls.authorizationNickname as UntypedFormControl;
  }

}
